import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  QrCode, Download, Loader2, Plus, BookOpen,
  CheckCircle2, Copy, Package
} from "lucide-react";

const AdminCodeGeneratorPage = () => {
  const { t } = useTranslation('platform');
  const [batches, setBatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [bookId, setBookId] = useState("BOOK-I");
  const [count, setCount] = useState(100);
  const [batchLabel, setBatchLabel] = useState("");
  const [generating, setGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [batchRes, statsRes] = await Promise.all([
        axios.get(`${API}/reader-hub/admin/batches`, { withCredentials: true }),
        axios.get(`${API}/reader-hub/admin/stats`, { withCredentials: true }),
      ]);
      setBatches(batchRes.data.batches || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/reader-hub/admin/generate-codes`, {
        book_id: bookId,
        count,
        batch_label: batchLabel,
      }, { withCredentials: true });
      setLastGenerated(res.data);
      fetchData();
    } catch (err) {
      console.error("Generation failed:", err);
    } finally {
      setGenerating(false);
    }
  };

  const downloadCSV = async (batchId) => {
    try {
      const res = await axios.get(`${API}/reader-hub/admin/batch/${batchId}/export`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${batchId}_codes.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const copyAllCodes = () => {
    if (!lastGenerated?.codes) return;
    navigator.clipboard.writeText(lastGenerated.codes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <QMSLayout title={t('platform:codeGenerator.title')}>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-code-generator">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="code-stats">
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4">
              <div className="text-2xl font-bold text-white">{stats.total_codes}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">Total Codes</div>
            </div>
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4">
              <div className="text-2xl font-bold text-emerald-400">{stats.activated_codes}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">Activated</div>
            </div>
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4">
              <div className="text-2xl font-bold text-[#00C8E0]">{stats.available_codes}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">Available</div>
            </div>
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4">
              <div className="text-2xl font-bold text-white">{stats.total_readers}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">Readers</div>
            </div>
          </div>
        )}

        {/* Generator form */}
        <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-6" data-testid="generate-form">
          <h2 className="text-base font-bold text-white mb-4 flex items-center gap-2">
            <QrCode className="w-5 h-5 text-[#00C8E0]" /> Generate Access Codes
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Book</label>
              <select
                value={bookId}
                onChange={(e) => setBookId(e.target.value)}
                className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white focus:border-[#1A6BCC] focus:outline-none"
                data-testid="book-select"
              >
                <option value="BOOK-I">I — Professional Engineering Reference Guides - Engineering Knowledge Base (BDIN-)</option>
                <option value="BOOK-II">II — Key Formula Models - Engineering Knowledge Base (BDII-)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Quantity</label>
              <input
                type="number"
                value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(10000, parseInt(e.target.value) || 1)))}
                min={1}
                max={10000}
                className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white focus:border-[#1A6BCC] focus:outline-none"
                data-testid="count-input"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Batch Label</label>
              <input
                type="text"
                value={batchLabel}
                onChange={(e) => setBatchLabel(e.target.value)}
                placeholder="e.g., Print Run March 2026"
                className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-[#1A6BCC] focus:outline-none"
                data-testid="batch-label-input"
              />
            </div>
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="bg-[#1A6BCC] hover:bg-[#1A6BCC]/80 disabled:opacity-40 text-white px-6 py-2.5 rounded text-sm font-bold flex items-center gap-2 transition-colors"
            data-testid="generate-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Generate {count} Codes
          </button>
        </div>

        {/* Last generated result */}
        {lastGenerated && (
          <div className="bg-[#0D1F38] border border-emerald-800/30 rounded-lg p-6" data-testid="last-generated">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-emerald-300 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Generated {lastGenerated.codes_generated} codes
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={copyAllCodes}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white bg-[#132340] border border-[#1E3A5F] rounded px-3 py-1.5 transition-colors"
                  data-testid="copy-codes-btn"
                >
                  <Copy className="w-3.5 h-3.5" /> {copied ? "Copied!" : "Copy All"}
                </button>
                <button
                  onClick={() => downloadCSV(lastGenerated.batch_id)}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white bg-[#132340] border border-[#1E3A5F] rounded px-3 py-1.5 transition-colors"
                  data-testid="download-csv-btn"
                >
                  <Download className="w-3.5 h-3.5" /> CSV
                </button>
              </div>
            </div>
            <div className="bg-[#0A1628] rounded p-3 max-h-48 overflow-y-auto font-mono text-xs text-slate-300 space-y-0.5">
              {lastGenerated.codes.map((c, i) => (
                <div key={i}>{c}</div>
              ))}
            </div>
            <div className="text-[10px] text-slate-500 mt-2">
              Batch: {lastGenerated.batch_id} | Book: {lastGenerated.book_id}
              {lastGenerated.batch_label && ` | ${lastGenerated.batch_label}`}
            </div>
          </div>
        )}

        {/* Batch history */}
        <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-6" data-testid="batch-history">
          <h2 className="text-base font-bold text-white mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-slate-400" /> Batch History
          </h2>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#1A6BCC] animate-spin" /></div>
          ) : batches.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">No batches generated yet.</p>
          ) : (
            <div className="space-y-2">
              {batches.map((b) => (
                <div key={b.batch_id} className="flex items-center gap-4 bg-[#0A1628] border border-[#1E3A5F]/50 rounded p-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white truncate">
                      {b.batch_id}
                      {b.batch_label && <span className="text-slate-400 font-normal ml-2">{b.batch_label}</span>}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {b.book_id} | {b.total_codes} codes | {b.activated_codes} activated | {b.created_at?.split("T")[0]}
                    </div>
                  </div>
                  <button
                    onClick={() => downloadCSV(b.batch_id)}
                    className="flex items-center gap-1.5 text-xs text-[#00C8E0] hover:text-white shrink-0"
                    data-testid={`download-batch-${b.batch_id}`}
                  >
                    <Download className="w-3.5 h-3.5" /> CSV
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </QMSLayout>
  );
};

export default AdminCodeGeneratorPage;
